// contexts/PWAContext.tsx

/// <reference types="vite-plugin-pwa/client" />

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { registerSW } from 'virtual:pwa-register';

import {
  getPWAInfo,
  onDisplayModeChange,
  checkForServiceWorkerUpdate,
  PWAInfo,
  AppDisplayMode,
} from '@/utils/pwaDetection';

import { useVisibilityAwareInterval } from '../PageVisibilityContext';

// ============================================
// Types
// ============================================

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAContextValue extends PWAInfo {
  /** Trigger the native install prompt (if available) */
  promptInstall: () => Promise<boolean>;
  /** Whether the install prompt is available */
  installPromptAvailable: boolean;
  /** Dismiss the install prompt for this session */
  dismissInstallPrompt: () => void;
  /** Whether the user has dismissed the install prompt */
  installPromptDismissed: boolean;
  /** Whether an app update is available */
  updateAvailable: boolean;
  /** Apply the pending update (reloads the page) */
  applyUpdate: () => void;
  /** Manually check for updates */
  checkForUpdates: () => Promise<void>;
  /** Dismiss the update prompt for this session */
  dismissUpdate: () => void;
  /** Whether the update prompt has been dismissed */
  updateDismissed: boolean;
  /** Whether currently checking for updates */
  isCheckingUpdate: boolean;
}

interface PWAProviderProps {
  children: ReactNode;
  /** How often to check for updates when active (ms). Default: 5 minutes */
  updateCheckInterval?: number;
  /** How often to check for updates when inactive (ms). Default: disabled */
  inactiveUpdateCheckInterval?: number;
  /** Auto-reload when update found. Default: false */
  autoUpdate?: boolean;
}

// ============================================
// Constants
// ============================================

const DEFAULT_UPDATE_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

// ============================================
// Context
// ============================================

const PWAContext = createContext<PWAContextValue | null>(null);

// ============================================
// Provider
// ============================================

export const PWAProvider: React.FC<PWAProviderProps> = ({
  children,
  updateCheckInterval = DEFAULT_UPDATE_CHECK_INTERVAL,
  inactiveUpdateCheckInterval,
  autoUpdate = false,
}) => {
  const [pwaInfo, setPwaInfo] = useState<PWAInfo>(getPWAInfo);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installPromptDismissed, setInstallPromptDismissed] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [updateSW, setUpdateSW] = useState<((reloadPage?: boolean) => Promise<void>) | null>(null);

  // ============================================
  // Initialize service worker registration
  // ============================================

  useEffect(() => {
    const sw = registerSW({
      immediate: true,

      onNeedRefresh() {
        setUpdateAvailable(true);
        setUpdateDismissed(false);

        if (autoUpdate) {
          setTimeout(() => {
            sw(true);
          }, 100);
        }
      },

      onOfflineReady() {
        console.log('App ready to work offline');
      },

      onRegisterError(error) {
        console.error('SW registration failed:', error);
      },
    });

    setUpdateSW(() => sw);
  }, [autoUpdate]);

  // ============================================
  // Visibility-aware update checking
  // ============================================

  useVisibilityAwareInterval(
    useCallback(async ({ isReturningToActive }) => {
      // Always check when returning to active, or on regular interval
      setIsCheckingUpdate(true);
      await checkForServiceWorkerUpdate();
      setIsCheckingUpdate(false);

      // Log for debugging
      if (isReturningToActive) {
        console.log('PWA: Checked for updates after returning to app');
      }
    }, []),
    updateCheckInterval,
    {
      enabled: true,
      inactiveIntervalMs: inactiveUpdateCheckInterval,
      fireImmediately: false
    }
  );

  // ============================================
  // Display mode changes
  // ============================================

  useEffect(() => {
    const unsubscribe = onDisplayModeChange((mode: AppDisplayMode) => {
      setPwaInfo((prev) => ({
        ...prev,
        displayMode: mode,
        isPWA: mode !== 'browser',
      }));
    });

    return unsubscribe;
  }, []);

  // ============================================
  // Install prompt event
  // ============================================

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // ============================================
  // App installed event
  // ============================================

  useEffect(() => {
    const handler = () => {
      setInstallPrompt(null);
      setPwaInfo(getPWAInfo());
    };

    window.addEventListener('appinstalled', handler);

    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  // ============================================
  // Service worker status check
  // ============================================

  useEffect(() => {
    const checkServiceWorker = () => {
      const hasActive = Boolean(navigator.serviceWorker?.controller);
      setPwaInfo((prev) => {
        if (prev.hasActiveServiceWorker === hasActive) {
          return prev;
        }
        return { ...prev, hasActiveServiceWorker: hasActive };
      });
    };

    checkServiceWorker();

    // Listen for controller changes
    navigator.serviceWorker?.addEventListener('controllerchange', checkServiceWorker);

    return () => {
      navigator.serviceWorker?.removeEventListener('controllerchange', checkServiceWorker);
    };
  }, []);

  // ============================================
  // Actions
  // ============================================

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!installPrompt) {
      return false;
    }

    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;

      if (outcome === 'accepted') {
        setInstallPrompt(null);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error prompting PWA install:', error);
      return false;
    }
  }, [installPrompt]);

  const dismissInstallPrompt = useCallback(() => {
    setInstallPromptDismissed(true);
  }, []);

  const applyUpdate = useCallback(() => {
    updateSW?.(true);
  }, [updateSW]);

  const checkForUpdates = useCallback(async () => {
    setIsCheckingUpdate(true);
    await checkForServiceWorkerUpdate();
    setIsCheckingUpdate(false);
  }, []);

  const dismissUpdate = useCallback(() => {
    setUpdateDismissed(true);
  }, []);

  // ============================================
  // Context value
  // ============================================

  const installPromptAvailable = installPrompt !== null && !installPromptDismissed;

  const value = useMemo<PWAContextValue>(
    () => ({
      ...pwaInfo,
      promptInstall,
      installPromptAvailable,
      dismissInstallPrompt,
      installPromptDismissed,
      updateAvailable: updateAvailable && !updateDismissed,
      applyUpdate,
      checkForUpdates,
      dismissUpdate,
      updateDismissed,
      isCheckingUpdate,
    }),
    [
      pwaInfo,
      promptInstall,
      installPromptAvailable,
      dismissInstallPrompt,
      installPromptDismissed,
      updateAvailable,
      updateDismissed,
      applyUpdate,
      checkForUpdates,
      dismissUpdate,
      isCheckingUpdate,
    ]
  );

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
};

// ============================================
// Hooks
// ============================================

export const usePWA = (): PWAContextValue => {
  const context = useContext(PWAContext);

  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }

  return context;
};

export const useIsPWA = (): boolean => {
  return usePWA().isPWA;
};

export const useInstallPrompt = () => {
  const { installPromptAvailable, promptInstall, dismissInstallPrompt, canInstall, platform } =
    usePWA();

  return useMemo(
    () => ({
      available: installPromptAvailable,
      prompt: promptInstall,
      dismiss: dismissInstallPrompt,
      canInstall,
      platform,
    }),
    [installPromptAvailable, promptInstall, dismissInstallPrompt, canInstall, platform]
  );
};

export const useAppUpdate = () => {
  const { updateAvailable, applyUpdate, checkForUpdates, dismissUpdate, isCheckingUpdate } =
    usePWA();

  return useMemo(
    () => ({
      available: updateAvailable,
      apply: applyUpdate,
      check: checkForUpdates,
      dismiss: dismissUpdate,
      isChecking: isCheckingUpdate,
    }),
    [updateAvailable, applyUpdate, checkForUpdates, dismissUpdate, isCheckingUpdate]
  );
};